import { Button } from "./ui/button";
import { Globe } from "lucide-react";
import { useState } from "react";
import { i18n } from "../i18n";

type Language = 'ru' | 'en';

interface LanguageToggleProps {
  currentLanguage: Language;
  onLanguageChange: (language: Language) => void;
  variant?: 'light' | 'dark';
}

export function LanguageToggle({ 
  currentLanguage, 
  onLanguageChange, 
  variant = 'light' 
}: LanguageToggleProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  const languages = [
    { code: 'ru' as const, name: 'Русский', flag: '🇷🇺' },
    { code: 'en' as const, name: 'English', flag: '🇺🇸' },
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage);

  const handleLanguageSelect = (newLang: Language) => {
    localStorage.setItem('plinus-language', newLang);
    onLanguageChange(newLang);
    void i18n.changeLanguage(newLang);
    setIsOpen(false);
    const path = window.location.pathname.replace(/^\/(ru|en)(\/.*)?$/, (_, __, rest) => rest || '/');
    // На странице share не добавляем языковой префикс в URL — язык из localStorage/браузера
    if (/^\/share\//.test(path)) {
      return;
    }
    window.location.href = `/${newLang}${path}${window.location.search}${window.location.hash}`;
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`gap-2 ${
          variant === 'dark' 
            ? 'text-white hover:bg-slate-700/50' 
            : 'text-gray-600 hover:bg-gray-100'
        }`}
      >
        <Globe className="w-4 h-4" />
        <span className="text-sm">{currentLang?.flag}</span>
      </Button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className={`absolute right-0 top-full mt-1 z-50 min-w-[140px] rounded-md border shadow-lg ${
            variant === 'dark' 
              ? 'bg-slate-800 border-slate-700' 
              : 'bg-white border-gray-200'
          }`}>
            <div className="p-1">
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleLanguageSelect(language.code)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer transition-colors ${
                    currentLanguage === language.code 
                      ? (variant === 'dark' ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-900')
                      : (variant === 'dark' 
                          ? 'text-gray-300 hover:bg-slate-700 hover:text-white' 
                          : 'text-gray-700 hover:bg-gray-100')
                  }`}
                >
                  <span>{language.flag}</span>
                  <span>{language.name}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
